import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_PAGES, CONTACT } from "./data";
import { scrollToId } from "./motion";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goQuote = () => {
    setOpen(false);
    if (document.getElementById("quote")) scrollToId("quote");
    else navigate("/#quote");
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#1c1c1c]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label="SolarArk home" onClick={() => setOpen(false)}>
          <img src="/solarark-logo-2026.png" alt="SolarArk Australia" className="h-14 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_PAGES.map((l) => {
            const active = l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={`text-sm tracking-wide transition-colors duration-300 relative group ${active ? "text-[#fcc41b]" : "text-zinc-300 hover:text-white"}`}
              >
                {l.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-[#fcc41b] transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-phone-link"
            className="hidden lg:inline-flex items-center gap-2 text-sm font-display font-semibold text-white hover:text-[#fcc41b] transition-colors duration-300"
          >
            <Phone size={16} className="text-[#fcc41b]" />
            {CONTACT.phone}
          </a>
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-phone-icon"
            aria-label={`Call SolarArk on ${CONTACT.phone}`}
            className="sm:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#fcc41b]/40 text-[#fcc41b] active:scale-95 transition-transform duration-300"
          >
            <Phone size={18} />
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={goQuote}
            className="hidden sm:inline-flex items-center rounded-full bg-[#fcc41b] text-black font-display font-semibold text-sm px-6 py-2.5 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300"
          >
            Request a Quote
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden text-white p-2" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-[#1c1c1c]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_PAGES.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`nav-mobile-link-${l.to.replace("/", "")}`} onClick={() => setOpen(false)} className="text-left text-lg font-display text-zinc-200">
                  {l.label}
                </Link>
              ))}
              <button onClick={goQuote} className="rounded-full bg-[#fcc41b] text-black font-semibold px-6 py-3 mt-2">
                Request a Quote
              </button>
              <a
                href={CONTACT.phoneHref}
                data-testid="nav-mobile-phone"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#fcc41b]/40 text-[#fcc41b] font-display font-semibold px-6 py-3"
              >
                <Phone size={17} />
                Call {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
